/* eslint-disable */
import apiClient from './apiClient';

interface User {
    id: number;
    name: string;
    lastname: string;
    email: string;
    username?: string | null;
    active_directory?: string;
    description?: string;
    last_session?: string;
    status: 'A' | 'I';
    is_admin?: boolean;
    profile_id?: number;
    created_at?: string;
    updated_at?: string;
    profile?: { id: number; name: string; description?: string };
    /** @deprecated Kept for backward compatibility. Use is_admin instead. */
    roles?: Array<{ id: number; name: string; description: string }>;
}

interface Role {
    id: number;
    name: string;
    description: string;
    created_at?: string;
    updated_at?: string;
    users?: User[];
}

interface Profile {
    id: number;
    name: string;
    description?: string;
    is_active?: boolean;
    created_at?: string;
    updated_at?: string;
    modules?: ProfileModule[];
}

interface ProfileModule {
    id: number;
    name: string;
    description?: string;
    code: string;
    is_active: boolean;
    profile_id: number;
    actions?: ProfileModuleAction[];
}

interface ProfileModuleAction {
    id: number;
    name: string;
    description?: string;
    code: string;
    is_active: boolean;
    profile_module_id: number;
}

interface PaginatedResponse<T> {
    data: T[];
    total: number;
    per_page: number;
    current_page: number;
    last_page?: number;
}

interface ApiResponse<T> {
    success: boolean;
    data: T;
    message?: string;
}

interface CreateUserRequest {
    name: string;
    lastname: string;
    email: string;
    username: string;
    password: string;
    active_directory?: string;
    description?: string;
    status?: 'A' | 'I';
    profile_id?: number;
    is_admin?: boolean;
    role_ids?: number[];
}

interface CreateRoleRequest {
    name: string;
    description: string;
}

interface CreateProfileRequest {
    name: string;
    description?: string;
}

// User Service
export const userService = {
    async getAll(params: { page?: number; per_page?: number; search?: string; status?: string; profile_id?: number } = {}): Promise<PaginatedResponse<User>> {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page.toString());
        if (params.per_page) queryParams.append('per_page', params.per_page.toString());
        if (params.search) queryParams.append('search', params.search);
        if (params.status) queryParams.append('status', params.status);
        if (params.profile_id) queryParams.append('profile_id', params.profile_id.toString());

        const response = await apiClient.get<PaginatedResponse<User>>(`/users?${queryParams.toString()}`);
        return response.data;
    },

    async getById(id: number): Promise<User> {
        const response = await apiClient.get<ApiResponse<User>>(`/users/${id}`);
        return response.data.data;
    },

    async create(data: CreateUserRequest): Promise<User> {
        const response = await apiClient.post<ApiResponse<User>>('/users', data);
        return response.data.data;
    },

    async update(id: number, data: Partial<CreateUserRequest>): Promise<User> {
        const response = await apiClient.put<ApiResponse<User>>(`/users/${id}`, data);
        return response.data.data;
    },

    async delete(id: number): Promise<void> {
        await apiClient.delete(`/users/${id}`);
    },

    async toggleStatus(id: number): Promise<User> {
        const response = await apiClient.patch<ApiResponse<User>>(`/users/${id}/toggle-status`);
        return response.data.data;
    },
};

// Role Service
export const roleService = {
    async getAll(params: { page?: number; per_page?: number; search?: string } = {}): Promise<PaginatedResponse<Role>> {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page.toString());
        if (params.per_page) queryParams.append('per_page', params.per_page.toString());
        if (params.search) queryParams.append('search', params.search);

        const response = await apiClient.get<PaginatedResponse<Role>>(`/roles?${queryParams.toString()}`);
        return response.data;
    },

    async getById(id: number): Promise<Role> {
        const response = await apiClient.get<ApiResponse<Role>>(`/roles/${id}`);
        return response.data.data;
    },

    async create(data: CreateRoleRequest): Promise<Role> {
        const response = await apiClient.post<ApiResponse<Role>>('/roles', data);
        return response.data.data;
    },

    async update(id: number, data: Partial<CreateRoleRequest>): Promise<Role> {
        const response = await apiClient.put<ApiResponse<Role>>(`/roles/${id}`, data);
        return response.data.data;
    },

    async delete(id: number): Promise<void> {
        await apiClient.delete(`/roles/${id}`);
    },

    async assignUsers(roleId: number, userIds: number[]): Promise<Role> {
        const response = await apiClient.post<ApiResponse<Role>>(`/roles/${roleId}/assign-users`, { user_ids: userIds });
        return response.data.data;
    },
};

// Profile Service
export const profileService = {
    async getAll(params: { page?: number; per_page?: number; search?: string } = {}): Promise<PaginatedResponse<Profile>> {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page.toString());
        if (params.per_page) queryParams.append('per_page', params.per_page.toString());
        if (params.search) queryParams.append('search', params.search);

        const response = await apiClient.get<PaginatedResponse<Profile>>(`/profiles?${queryParams.toString()}`);
        return response.data;
    },

    async getById(id: number): Promise<Profile> {
        const response = await apiClient.get<ApiResponse<Profile>>(`/profiles/${id}`);
        return response.data.data;
    },

    async create(data: CreateProfileRequest): Promise<Profile> {
        const response = await apiClient.post<ApiResponse<Profile>>('/profiles', data);
        return response.data.data;
    },

    async update(id: number, data: Partial<CreateProfileRequest>): Promise<Profile> {
        const response = await apiClient.put<ApiResponse<Profile>>(`/profiles/${id}`, data);
        return response.data.data;
    },

    async delete(id: number): Promise<void> {
        await apiClient.delete(`/profiles/${id}`);
    },

    async addModule(profileId: number, data: { name: string; description?: string; code: string; is_active?: boolean }): Promise<ProfileModule> {
        const response = await apiClient.post<ApiResponse<ProfileModule>>(`/profiles/${profileId}/modules`, data);
        return response.data.data;
    },

    async updateModule(moduleId: number, data: Partial<{ name: string; description: string; code: string; is_active: boolean }>): Promise<ProfileModule> {
        const response = await apiClient.put<ApiResponse<ProfileModule>>(`/profiles/modules/${moduleId}`, data);
        return response.data.data;
    },

    async deleteModule(moduleId: number): Promise<void> {
        await apiClient.delete(`/profiles/modules/${moduleId}`);
    },

    async addAction(moduleId: number, data: { name: string; description?: string; code: string; is_active?: boolean }): Promise<ProfileModuleAction> {
        const response = await apiClient.post<ApiResponse<ProfileModuleAction>>(`/profiles/modules/${moduleId}/actions`, data);
        return response.data.data;
    },

    async updateAction(actionId: number, data: Partial<{ name: string; description: string; code: string; is_active: boolean }>): Promise<ProfileModuleAction> {
        const response = await apiClient.put<ApiResponse<ProfileModuleAction>>(`/profiles/actions/${actionId}`, data);
        return response.data.data;
    },

    async deleteAction(actionId: number): Promise<void> {
        await apiClient.delete(`/profiles/actions/${actionId}`);
    },

    async getProfileModules(profileId: number): Promise<ProfileModule[]> {
        const response = await apiClient.get<ApiResponse<ProfileModule[]>>(`/profiles/${profileId}/modules`);
        return response.data.data;
    },

    async setModuleActions(profileId: number, moduleId: number, actions: string[]): Promise<void> {
        await apiClient.put(`/profiles/${profileId}/modules/${moduleId}/actions`, { actions });
    },
};

export default { userService, roleService, profileService };
